import React, { useContext, useEffect, useMemo, useState } from "react";
import $api from "../../../../http";
import { Context } from "../../../../index";

type CommandGroup = "ММБ" | "СМБ";

interface CommandDef {
  id: string;
  label: string;
  type: string;
  payload: Record<string, unknown>;
  group: CommandGroup;
}

const COMMANDS: CommandDef[] = [
  {
    id: "mmb-reboot",
    label: "reboot",
    type: "reboot",
    payload: {},
    group: "ММБ",
  },
  {
    id: "mmb-get_configuration",
    label: "get_configuration",
    type: "get_configuration",
    payload: {},
    group: "ММБ",
  },
  {
    id: "mmb-change_configuration",
    label: "change_configuration",
    type: "change_configuration",
    payload: {
      telemetry_period_sec: 30,
      current_limit_line: 150,
    },
    group: "ММБ",
  },
  {
    id: "mmb-ota_start",
    label: "ota_start",
    type: "ota_start",
    payload: {
      firmware_url: "https://example.com/firmware.bin",
    },
    group: "ММБ",
  },
  {
    id: "mmb-boot_req",
    label: "boot_req",
    type: "boot_req",
    payload: {},
    group: "ММБ",
  },
  {
    id: "smb-ota_start",
    label: "ota_start",
    type: "ota_start",
    payload: {
      link_esp: "https://example.com/esp.bin",
    },
    group: "СМБ",
  },
  {
    id: "smb-station_reset",
    label: "station_reset",
    type: "station_reset",
    payload: { address: 10 },
    group: "СМБ",
  },
  {
    id: "smb-change_availability",
    label: "change_availability",
    type: "change_availability",
    payload: { address: 11, status: "inoperative" },
    group: "СМБ",
  },
  {
    id: "smb-unlock_connector",
    label: "unlock_connector",
    type: "unlock_connector",
    payload: { address: 12 },
    group: "СМБ",
  },
];

const commandToText = (command: CommandDef): string =>
  JSON.stringify({ type: command.type, payload: command.payload }, null, 2);

const textToCommand = (
  text: string,
): { type: string; payload: Record<string, unknown> } | null => {
  try {
    const parsed = JSON.parse(text);
    if (typeof parsed !== "object" || parsed === null) {
      return null;
    }
    return {
      type: typeof parsed.type === "string" ? parsed.type : "",
      payload:
        parsed.payload && typeof parsed.payload === "object"
          ? parsed.payload
          : {},
    };
  } catch {
    return null;
  }
};

export const NewProtocolCommandPanel: React.FC = () => {
  const { store } = useContext(Context);

  const [deviceId, setDeviceId] = useState("TEST_MMB_001");
  const [commandText, setCommandText] = useState("");
  const [sending, setSending] = useState(false);
  const [commandStatus, setCommandStatus] = useState<{
    type: "success" | "error" | null;
    message: string;
  }>({ type: null, message: "" });

  const deviceOptions = useMemo(() => {
    const ids = new Set<string>();
    store.modules?.forEach((module: any) => {
      if (module.charge_id) {
        ids.add(module.charge_id);
      }
    });
    return Array.from(ids).sort();
  }, [store.modules]);

  useEffect(() => {
    if (deviceOptions.length > 0 && !deviceOptions.includes(deviceId)) {
      setDeviceId(deviceOptions[0]);
    }
  }, [deviceOptions, deviceId]);

  useEffect(() => {
    if (!store.modules.length) {
      store.getAllModules();
    }
  }, [store]);

  const sendCommand = async () => {
    const command = textToCommand(commandText);
    if (!command || !command.type) {
      setCommandStatus({
        type: "error",
        message: "Некорректное содержание команды (ожидается JSON).",
      });
      return;
    }

    setSending(true);
    setCommandStatus({ type: null, message: "" });

    try {
      // БЕЗ-01/БЕЗ-05 (DEV-55): сессия — cookie, изменяющий запрос несёт
      // анти-CSRF-признак; отдельный Bearer-токен из хранилища не нужен.
      const response = await $api.post("/new-protocol-command", {
        device_id: deviceId,
        type: command.type,
        payload: command.payload,
      });

      setCommandStatus({
        type: "success",
        message: `${command.type} → OK${
          response.data ? ": " + JSON.stringify(response.data) : ""
        }`,
      });
    } catch (err: any) {
      const message =
        err?.response?.data?.message || err?.message || "Неизвестная ошибка";
      setCommandStatus({
        type: "error",
        message: `${command.type} → ${message}`,
      });
    } finally {
      setSending(false);
    }
  };

  const mmbCommands = COMMANDS.filter((cmd) => cmd.group === "ММБ");
  const smbCommands = COMMANDS.filter((cmd) => cmd.group === "СМБ");

  return (
    <div className="command-panel">
      <div className="command-device">
        <label htmlFor="device-id">device_id:</label>
        {deviceOptions.length > 0 ? (
          <select
            id="device-id"
            className="device-select"
            value={deviceId}
            onChange={(e) => setDeviceId(e.target.value)}
          >
            <option value="" disabled>
              -- выберите device_id --
            </option>
            {deviceOptions.map((id) => (
              <option key={id} value={id}>
                {id}
              </option>
            ))}
          </select>
        ) : (
          <input
            id="device-id"
            type="text"
            value={deviceId}
            onChange={(e) => setDeviceId(e.target.value)}
            placeholder="TEST_MMB_001"
          />
        )}
      </div>

      <div className="command-editor">
        <label htmlFor="command-content">Команда (JSON):</label>
        <textarea
          id="command-content"
          className="command-textarea"
          rows={5}
          value={commandText}
          onChange={(e) => setCommandText(e.target.value)}
          placeholder='{"type": "reboot", "payload": {}}'
          spellCheck={false}
        />
        <button
          onClick={sendCommand}
          disabled={sending}
          className="command-send-btn"
        >
          {sending ? "Отправка…" : "Отправить"}
        </button>
      </div>

      <div className="command-groups">
        <div className="command-group">
          <span className="command-group-title">ММБ</span>
          <div className="command-buttons">
            {mmbCommands.map((cmd) => (
              <button
                key={cmd.id}
                onClick={() => setCommandText(commandToText(cmd))}
                className="command-btn"
              >
                {cmd.label}
              </button>
            ))}
          </div>
        </div>

        <div className="command-group">
          <span className="command-group-title">СМБ</span>
          <div className="command-buttons">
            {smbCommands.map((cmd) => (
              <button
                key={cmd.id}
                onClick={() => setCommandText(commandToText(cmd))}
                className="command-btn"
              >
                {cmd.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {commandStatus.type && (
        <p className={`command-status ${commandStatus.type}`}>
          {commandStatus.message}
        </p>
      )}
    </div>
  );
};
